import { addDays } from 'date-fns';
import { Permission } from '../models/Permissions';
import { AuditEvent, AuditHistoryResponse } from '../models/EventHistoryModels';
import { CloudProvider, DownloadableLicense, InstanceStatus, TaniumInstance } from '../types';
import { IdpResponse } from '../models/IdpModels';
import { Tenant } from '../models/TenantModels';
import { CaseRole, GetCaseGrantsResponse } from '../models/CaseManagementModels';

export const mockTenant: Tenant = {
  id: '1234',
  accountName: 'IT Pros',
  workspace: 'customer-identifier',
  region: 'us-e1',
  username: 'testuser@customer-identifier.mytanium.com',
  email: 'testuser@customer-identifier.com',
  verifiedDomains: ['customer-identifier.com'],
  verifiedCustomer: false,
  isMsp: true,
  caseManagementEnabled: false,
  selfServeBillingEnabled: false,
  mspInstanceLimit: 50,
};

export const mockEventHistory: AuditHistoryResponse = {
  data: [
    {
      event: AuditEvent.DomainDelete,
      createdAt: addDays(new Date(), -1).getTime(),
      message: 'fake domain delete event',
      details: {},
    },
    {
      event: AuditEvent.DomainCreate,
      createdAt: addDays(new Date(), -2).getTime(),
      message: 'fake domain create event',
      details: {},
    },
    {
      event: AuditEvent.UserUpdate,
      createdAt: addDays(new Date(), -3).getTime(),
      message: 'fake user update event',
      details: {},
    },
    {
      event: AuditEvent.UserCreate,
      createdAt: addDays(new Date(), -4).getTime(),
      message: 'fake user create event',
      details: {},
    },
  ],
  nextPageCursor: '',
  totalCount: 4,
};

export const idpResponses: IdpResponse[] = [
  {
    id: 'idpID',
    name: 'AName',
    displayName: 'AName',
    samlMetadataUrl: '',
    samlMetadataFile: 'fake-metadata-file',
    domainNames: ['ADomain.com'],
    emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    enableIdpSignout: false,
    enableTaniumConsoleAuth: false,
    enableAccountPortalAuth: true,
  },
  {
    id: 'idpID2',
    name: 'BName',
    displayName: 'BName',
    samlMetadataUrl:
      'https://login.microsoftonline.com/c48fd9e4-a55a-46b9-a824-c8f1daa22b1c/federationmetadata/2007-06/federationmetadata.xml?appid=b8360aa1-c041-41f8-b891-4df854f73b32',
    samlMetadataFile: '',
    domainNames: ['BDomain.com', 'test.com'],
    emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    enableIdpSignout: true,
    enableTaniumConsoleAuth: false,
    enableAccountPortalAuth: true,
  },
  {
    id: 'idpID3',
    name: 'CName',
    displayName: 'CName',
    samlMetadataUrl: '',
    samlMetadataFile: 'fake-metadata-file',
    domainNames: ['CDomain.com'],
    emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    enableIdpSignout: false,
    enableTaniumConsoleAuth: false,
    enableAccountPortalAuth: true,
  },
];

export const createIdpResponse: IdpResponse = {
  id: 'idpID',
  name: 'idpName',
  displayName: 'idpDisplayName',
  emailAttributeName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
  samlMetadataUrl:
    'https://login.microsoftonline.com/c48fd9e4-a55a-46b9-a824-c8f1daa22b1c/federationmetadata/2007-06/federationmetadata.xml?appid=b8360aa1-c041-41f8-b891-4df854f73b32',
  samlMetadataFile: '',
  enableTaniumConsoleAuth: false,
  enableAccountPortalAuth: true,
  enableIdpSignout: true,
  domainNames: ['example.com', 'test.com'],
};

export const mockTaniumInstances: TaniumInstance[] = [
  {
    id: '600f87db-fd43-4e31-a01f-bcdb55f6a0d9',
    licenseId: '25415',
    createdAt: '2024-01-01',
    url: 'name 1',
    friendlyName: 'Dev Instance 1',
    instanceName: 'dev25415',
    region: 'us-e1',
    cloudProvider: CloudProvider.AZURE,
    isCloudInstance: true,
    status: InstanceStatus.Active,
    endpointCount: 100,
    licenseExpirationDate: '2025-12-31',
  },
  {
    id: '9dk39d8-5f1e-4af9-a37d-143a08b5c587',
    licenseId: '11875',
    createdAt: new Date().toISOString(),
    url: 'name 2',
    friendlyName: 'Dev Instance 2',
    instanceName: 'dev11875',
    region: 'us-e1',
    cloudProvider: CloudProvider.AWS,
    isCloudInstance: true,
    status: InstanceStatus.Creating,
    licenseExpirationDate: '2026-06-15',
  },
  {
    id: '9dk39d8-5f1e-4af9-a37d-143a08b5c587',
    licenseId: '44578',
    createdAt: new Date().toISOString(),
    url: 'name 3',
    friendlyName: 'Dev Instance 3',
    instanceName: 'dev44578',
    region: 'us-e1',
    cloudProvider: CloudProvider.AWS,
    isCloudInstance: true,
    status: InstanceStatus.Deactivating,
    licenseExpirationDate: '2024-09-01',
  },
  {
    id: 'prd-123',
    licenseId: '235152',
    createdAt: '2023-01-01',
    url: 'On-prem instance 4',
    friendlyName: 'On-prem Instance',
    instanceName: 'onprem-123',
    region: 'us-e1',
    isCloudInstance: false,
    status: InstanceStatus.Deactivating,
    downloadable: true,
  },
  {
    id: '9dk39d8-5f1e-4af9-a37d-143a08b5c587',
    licenseId: '47896',
    createdAt: new Date().toISOString(),
    url: 'www.tanium.com',
    friendlyName: 'Dev Instance 5',
    instanceName: 'dev44578',
    region: 'us-e1',
    cloudProvider: CloudProvider.AZURE,
    isCloudInstance: true,
    status: InstanceStatus.Deactivated,
  },
  {
    id: '9dk39d8-5f1e-4af9-a37d-143a08b5c587',
    licenseId: '56789',
    createdAt: new Date().toISOString(),
    url: 'deletedhost',
    friendlyName: 'Deleted Instance',
    instanceName: 'dev44599',
    region: 'us-e1',
    cloudProvider: CloudProvider.AZURE,
    isCloudInstance: true,
    status: InstanceStatus.Deleted,
  },
];

export const mockDownloadableLicenses: DownloadableLicense[] = [
  {
    licenseId: 987654,
    customerName: 'Globex Corporation',
    expiresAt: '2027-06-30',
    notifiedAt: '2026-07-01T12:00:00Z',
    isAvailable: true,
    sdrId: 'SDR-00042',
    // A multi-server deployment, so the Tanium Instance column exercises the truncated hostname cell.
    hostnames: 'ts1.globex.com,ts2.globex.com,ts3.globex.com',
  },
  {
    licenseId: 235152,
    instanceId: 'prd-123',
    customerName: 'IT Pros',
    url: 'On-prem instance 4',
    friendlyName: 'On-prem Instance',
    expiresAt: '2026-12-31',
    notifiedAt: '2026-01-15T12:00:00Z',
    downloadedAt: '2026-01-16T09:30:00Z',
  },
  // An expired pending offer, so both the disabled download and the enabled removal are exercisable in mock mode.
  {
    licenseId: 112233,
    customerName: 'Initech',
    expiresAt: '2025-03-31',
    notifiedAt: '2025-02-01T12:00:00Z',
    isAvailable: true,
    sdrId: 'SDR-00043',
    hostnames: 'ts1.initech.example',
    expired: true,
  },
];

export const mockPermissions = Object.values(Permission);

export const mockCaseGrantsResponse: GetCaseGrantsResponse = {
  accounts: [
    {
      crmAccountId: '0013200001D3n0nAAB',
      sharedWithOtherTenants: true,
      grants: [
        {
          email: 'admin@customer-identifier.com',
          role: CaseRole.CaseAdmin,
          updatedAt: Date.now(),
          actor: { username: 'testuser', email: 'testuser@customer-identifier.com' },
        },
        {
          email: 'user@customer-identifier.com',
          role: CaseRole.CaseUser,
          updatedAt: Date.now(),
          actor: { username: 'testuser', email: 'testuser@customer-identifier.com' },
        },
      ],
    },
  ],
};
